import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Tooltip from '../Tooltip';
import { OVERFLOW_COUNT_PREFIX } from './constants';
import { partitionVisibleItems } from './helpers';

/**
 * Row of chips that collapses everything beyond `maxVisible` into a trailing
 * "+N" count chip (the Figma "Tag — in use" overflow). Clicking the count
 * reveals the hidden chips inline and hides itself, unless `onOverflowClick`
 * takes over the click.
 */
const ChipGroup = ({
    children,
    maxVisible,
    onOverflowClick,
    overflowTooltip,
    overflowLabel,
    className,
}) => {
    const [expanded, setExpanded] = useState(false);

    const items = React.Children.toArray(children);
    const { visible, hidden } = partitionVisibleItems(items, expanded ? undefined : maxVisible);
    const hiddenCount = hidden.length;

    const renderOverflow = () => {
        if (hiddenCount <= 0) return null;

        const countText = `${OVERFLOW_COUNT_PREFIX}${hiddenCount}`;
        const customClick = typeof onOverflowClick === 'function';

        const trigger = (
            <button
                type="button"
                className="chip chip--overflow"
                aria-label={overflowLabel || countText}
                aria-expanded={customClick ? undefined : false}
                onClick={customClick ? onOverflowClick : () => setExpanded(true)}
            >
                {countText}
            </button>
        );

        if (!overflowTooltip) return trigger;

        return <Tooltip title={overflowTooltip}>{trigger}</Tooltip>;
    };

    if (items.length === 0) return null;

    return (
        <div className={classNames('chip-group', className)}>
            {visible}
            {renderOverflow()}
        </div>
    );
};

ChipGroup.propTypes = {
    /** Chips to render; anything past `maxVisible` collapses into the count chip */
    children: PropTypes.node,
    /** How many chips stay visible; `0` hides all, omit to show everything */
    maxVisible: PropTypes.number,
    /** Replaces the default expand-inline click behavior of the "+N" chip */
    onOverflowClick: PropTypes.func,
    /** Hover tooltip on the "+N" chip (e.g. a summary like "4 more tags") */
    overflowTooltip: PropTypes.string,
    /** Accessible label for the "+N" chip; defaults to the visible "+N" text */
    overflowLabel: PropTypes.string,
    /** Extra class on the group wrapper */
    className: PropTypes.string,
};

export default ChipGroup;
